import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import {
  Flex,
  LineChart,
  CurrencyValue,
  formatNumber,
  formatCurrency,
  DisplaySwitch
} from '@blockanalitica/ui';
import { useMemo, useState, useCallback } from 'react';
import { useChartPeriod } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';

export default function HistoricTabs({ ilk }) {
  const [activeDisplayOption, setActiveDisplayOption] = useState('debt');
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();
  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );
  const displayOptions = useMemo(
    () => [{ key: 'debt', value: 'Total Debt' }],
    []
  );

  const [data, error] = useApi({
    url: `/groups/legacy-rwa/ilks/${ilk}/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const valueFormatter = useCallback(
    (value: number) => {
      switch (activeDisplayOption) {
        case 'revenue':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: 'USD',
                iconSize: '3'
              }}
            />
          );
        default:
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('DAI'),
                iconSize: '3'
              }}
            />
          );
      }
    },
    [activeDisplayOption]
  );
  const yScaleValueFormatter = useCallback(
    (value: number) => {
      switch (activeDisplayOption) {
        case 'revenue':
          return formatCurrency(value);
        default:
          return formatNumber(value);
      }
    },
    [activeDisplayOption]
  );

  if (error) {
    throw error;
  }

  if (data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />

        <LineChart
          key={activeDisplayOption}
          data={data}
          datasets={[
            {
              xField: 'date',
              yField: activeDisplayOption
            }
          ]}
          timeFilter={timeFilter}
          valueFormatter={valueFormatter}
          yScaleValueFormatter={yScaleValueFormatter}
          config={config}
        />
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
